import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { Sun, Moon, Robot, ChartBar, Megaphone, Users, Shield, Coins, CalendarBlank, FileText, DeviceMobile, Lightning, Broadcast, ArrowRight, CheckCircle } from '@phosphor-icons/react';

const IMAGES = {
  hero: '/images/hero.png',
  bot: '/images/bot.png',
  broadcast: '/images/broadcast.png',
  analytics: '/images/analytics.png',
};

const features = [
  { icon: Robot, title: 'Multi-Bot Management', desc: 'Add unlimited bots via BotFather tokens. Auto-fetch details, ping health, assign to admins with permissions.', tag: 'Core' },
  { icon: Megaphone, title: 'Smart Broadcasting', desc: 'Photos, GIFs, videos, polls. Text 1 & Text 2 as separate messages with inline URL buttons.', tag: 'Core' },
  { icon: Broadcast, title: 'Channel Broadcasting', desc: 'Broadcast to Telegram channels. Add bot as admin, send to multiple channels simultaneously.', tag: 'New' },
  { icon: CalendarBlank, title: 'Scheduled Broadcasts', desc: 'Schedule user & channel broadcasts for any future IST date/time with auto-execution.', tag: 'Automation' },
  { icon: Users, title: 'Admin & Permissions', desc: 'Create admins, assign bots, granular permissions per feature. One-click login links.', tag: 'Security' },
  { icon: Shield, title: 'Role-Based Access', desc: 'Super Admin controls all. Admins see only assigned bots and permitted features.', tag: 'Security' },
  { icon: Coins, title: 'Credit Token System', desc: '1 credit = 1 delivery. Balance, usage, refunds tracking. Auto-refund failed sends.', tag: 'Billing' },
  { icon: ChartBar, title: 'Analytics Dashboard', desc: 'Real-time emoji stats, Recharts graphs, user & channel broadcast metrics.', tag: 'Analytics' },
  { icon: FileText, title: 'CSV Reports & Templates', desc: 'Download user, broadcast, mobile reports as CSV. Save reusable broadcast templates.', tag: 'Reports' },
  { icon: DeviceMobile, title: 'Mobile Collection', desc: 'Collect phone numbers via Telegram contact sharing button. Full reports.', tag: 'Data' },
];

const plans = [
  { name: 'Starter', price: 'Free', period: '', desc: 'For small projects', features: ['1 Bot', '500 Users', 'Basic Broadcasts', 'User Reports', 'CSV Downloads', 'Dark/Light Mode'], cta: 'Start Free' },
  { name: 'Professional', price: '$29', period: '/mo', desc: 'For growing teams', features: ['5 Bots', '25K Users', 'Channel Broadcasting', 'Scheduled Broadcasts', 'Broadcast Templates', 'Credit System', 'Admin Management', 'Broadcast Preview', 'All Reports'], cta: 'Get Started', highlight: true },
  { name: 'Enterprise', price: '$99', period: '/mo', desc: 'For large operations', features: ['Unlimited Bots & Users', 'Unlimited Channels', 'Priority Support', 'Custom Integrations', 'API Access', 'White-Label', 'Dedicated Server', 'SLA Guarantee', 'Custom Analytics'], cta: 'Contact Sales' },
];

const showcases = [
  { img: IMAGES.bot, title: 'Bot Welcome Experience', desc: 'When users click /start, they receive a personalized welcome: image, separate text messages, inline URL buttons, and optional mobile number request.', items: ['Welcome Image (no caption)', 'Text 1 & Text 2 as separate messages', 'Inline URL Buttons below Text 2', 'Mobile Number Collection Button'] },
  { img: IMAGES.broadcast, title: 'Mass Broadcasting Engine', desc: 'Send to thousands of users and channels simultaneously. Support for photos, GIFs, videos, polls with inline buttons.', items: ['User + Channel Broadcasting', 'Schedule for Future Delivery', 'Real-time Success/Fail Tracking', 'Credit Token Deduction & Refund'] },
  { img: IMAGES.analytics, title: 'Real-Time Analytics', desc: 'Interactive dashboard with emoji stat cards, bar charts, area charts, pie charts. Track users, broadcasts, and channel performance.', items: ['Today/Yesterday/Week/Month Stats', 'Success Rate Percentages', 'Channel Broadcast Metrics', 'Credit Usage History'] },
];

const stats = [
  { value: '10M+', label: 'Messages Delivered' },
  { value: '99.9%', label: 'Uptime Guarantee' },
  { value: '50+', label: 'Countries Served' },
  { value: '24/7', label: 'Support Available' },
];

export default function LandingPage() {
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [activeShowcase, setActiveShowcase] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setActiveShowcase(p => (p + 1) % showcases.length), 6000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="min-h-screen bg-background overflow-hidden" data-testid="landing-page">
      {/* NAVBAR */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-border/50 bg-background/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/logo.png" alt="Telegram Bot" className="w-7 h-7 object-contain" />
            <span className="text-xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>TG Bot Admin</span>
          </div>
          <div className="hidden md:flex items-center gap-8 text-sm">
            <a href="#features" className="text-muted-foreground hover:text-foreground transition-colors">Features</a>
            <a href="#showcase" className="text-muted-foreground hover:text-foreground transition-colors">How It Works</a>
            <a href="#pricing" className="text-muted-foreground hover:text-foreground transition-colors">Pricing</a>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={toggleTheme} className="p-2 rounded-md border border-border hover:bg-accent transition-colors" data-testid="landing-theme-toggle">
              {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
            </button>
            <button onClick={() => navigate('/login')} className="px-5 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-md hover:opacity-90 transition-all active:scale-[0.97]" data-testid="landing-login-btn">
              Sign In
            </button>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative pt-28 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-20 right-1/4 w-80 h-80 bg-violet-500/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
        </div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-border text-xs font-medium text-muted-foreground mb-6 bg-muted/50 animate-fadeIn">
                <Lightning size={14} weight="fill" className="text-amber-500" /> Enterprise Telegram Bot Platform
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.08] mb-6 animate-slideUp" style={{ fontFamily: 'Cabinet Grotesk' }}>
                The Ultimate<br />Telegram Bot<br />
                <span className="bg-gradient-to-r from-blue-600 via-violet-600 to-fuchsia-600 bg-clip-text text-transparent">Command Center</span>
              </h1>
              <p className="text-base md:text-lg text-muted-foreground max-w-lg mb-8 leading-relaxed animate-slideUp" style={{ animationDelay: '0.15s' }}>
                Manage bots, broadcast to users & channels, track analytics, control admin permissions, and handle credit tokens — all from one powerful dashboard.
              </p>
              <div className="flex flex-col sm:flex-row items-center gap-3 animate-slideUp" style={{ animationDelay: '0.3s' }}>
                <button onClick={() => navigate('/login')} className="px-8 py-3.5 text-sm font-semibold bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-all active:scale-[0.97] w-full sm:w-auto flex items-center justify-center gap-2" data-testid="hero-get-started">
                  Get Started Free <ArrowRight size={16} />
                </button>
                <a href="#showcase" className="px-8 py-3.5 text-sm font-semibold border border-border rounded-lg hover:bg-accent transition-all w-full sm:w-auto text-center">
                  See How It Works
                </a>
              </div>
            </div>
            <div className="relative animate-slideUp" style={{ animationDelay: '0.4s' }}>
              <div className="rounded-2xl overflow-hidden border border-border/50 shadow-2xl">
                <img src={IMAGES.hero} alt="Dashboard" className="w-full h-auto" loading="eager" />
              </div>
              <div className="absolute -bottom-4 -right-4 bg-background border border-border rounded-xl p-3 shadow-lg animate-float">
                <div className="flex items-center gap-2">
                  <span className="text-green-500 text-lg">&#10003;</span>
                  <div>
                    <p className="text-xs font-semibold">Broadcast Sent</p>
                    <p className="text-[10px] text-muted-foreground">1,234 users reached</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS BAR */}
      <section className="py-8 border-y border-border bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((s, i) => (
            <div key={i} className="text-center">
              <p className="font-mono text-2xl sm:text-3xl font-bold tracking-tight">{s.value}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="text-center mb-14">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Cabinet Grotesk' }}>Everything You Need to Scale</h2>
          <p className="text-sm md:text-base text-muted-foreground max-w-xl mx-auto">10+ enterprise features for complete Telegram bot operations</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <div key={i} className="group border border-border rounded-lg p-4 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 hover:border-foreground/20" data-testid={`feature-card-${i}`}>
                <div className="flex items-center gap-2 mb-2.5">
                  <div className="p-1.5 rounded-md bg-primary/5 group-hover:bg-primary/10 transition-colors">
                    <Icon size={18} weight="duotone" />
                  </div>
                  <span className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground px-1.5 py-0.5 rounded-full border border-border">{f.tag}</span>
                </div>
                <h3 className="font-semibold text-sm mb-1">{f.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{f.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* SHOWCASE - How It Works */}
      <section id="showcase" className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Cabinet Grotesk' }}>How It Works</h2>
            <p className="text-sm md:text-base text-muted-foreground">See the platform in action</p>
          </div>
          <div className="flex justify-center gap-2 mb-10">
            {showcases.map((s, i) => (
              <button key={i} onClick={() => setActiveShowcase(i)} className={`px-4 py-2 text-xs font-medium rounded-lg border transition-all duration-300 ${activeShowcase === i ? 'bg-primary text-primary-foreground border-primary shadow-lg' : 'border-border hover:bg-accent'}`}>
                {s.title}
              </button>
            ))}
          </div>
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div className="relative">
              <div className="rounded-2xl overflow-hidden border border-border shadow-xl">
                <img src={showcases[activeShowcase].img} alt={showcases[activeShowcase].title} className="w-full h-auto transition-opacity duration-500" />
              </div>
            </div>
            <div className="space-y-6">
              <h3 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>{showcases[activeShowcase].title}</h3>
              <p className="text-muted-foreground leading-relaxed">{showcases[activeShowcase].desc}</p>
              <ul className="space-y-3">
                {showcases[activeShowcase].items.map((item, j) => (
                  <li key={j} className="flex items-start gap-3 text-sm">
                    <CheckCircle size={18} weight="fill" className="text-green-500 shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <button onClick={() => navigate('/login')} className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-all">
                Try It Now <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="text-center mb-14">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Cabinet Grotesk' }}>Simple, Transparent Pricing</h2>
          <p className="text-sm md:text-base text-muted-foreground">Start free. Scale when you're ready.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {plans.map((plan, i) => (
            <div key={i} className={`rounded-xl border p-6 flex flex-col transition-all duration-300 hover:-translate-y-1 ${plan.highlight ? 'border-primary shadow-xl ring-1 ring-primary/20 relative' : 'border-border hover:shadow-lg'}`} data-testid={`pricing-${plan.name.toLowerCase()}`}>
              {plan.highlight && <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-0.5 bg-primary text-primary-foreground text-xs font-bold rounded-full whitespace-nowrap">Most Popular</div>}
              <h3 className="text-lg font-bold" style={{ fontFamily: 'Cabinet Grotesk' }}>{plan.name}</h3>
              <p className="text-xs text-muted-foreground mb-4">{plan.desc}</p>
              <div className="flex items-baseline gap-1 mb-5">
                <span className="font-mono text-4xl font-bold tracking-tight">{plan.price}</span>
                {plan.period && <span className="text-sm text-muted-foreground">{plan.period}</span>}
              </div>
              <ul className="space-y-2 flex-1 mb-6">
                {plan.features.map((f, j) => (
                  <li key={j} className="flex items-center gap-2 text-sm">
                    <CheckCircle size={14} weight="fill" className="text-green-500 shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <button onClick={() => navigate('/login')} className={`w-full py-2.5 text-sm font-medium rounded-lg transition-all active:scale-[0.97] ${plan.highlight ? 'bg-primary text-primary-foreground hover:opacity-90' : 'border border-border hover:bg-accent'}`}>
                {plan.cta}
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/30 border-t border-border">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Cabinet Grotesk' }}>Ready to Supercharge Your Bots?</h2>
          <p className="text-muted-foreground mb-8 max-w-lg mx-auto">Join hundreds of businesses managing their Telegram bots with our enterprise platform. Start free, no credit card required.</p>
          <button onClick={() => navigate('/login')} className="px-10 py-4 text-sm font-semibold bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-all active:scale-[0.97] inline-flex items-center gap-2" data-testid="cta-get-started">
            Get Started Now <ArrowRight size={16} />
          </button>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-border py-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="Telegram Bot" className="w-5 h-5 object-contain" />
            <span className="font-bold text-sm" style={{ fontFamily: 'Cabinet Grotesk' }}>TG Bot Admin</span>
          </div>
          <p className="text-xs text-muted-foreground text-center">Enterprise Telegram Bot Management. All times IST (UTC+5:30).</p>
          <div className="flex gap-4 text-xs text-muted-foreground">
            <a href="#features" className="hover:text-foreground transition-colors">Features</a>
            <a href="#pricing" className="hover:text-foreground transition-colors">Pricing</a>
            <button onClick={() => navigate('/login')} className="hover:text-foreground transition-colors">Dashboard</button>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
        .animate-fadeIn { animation: fadeIn 0.6s ease-out both; }
        .animate-slideUp { animation: slideUp 0.7s ease-out both; }
        .animate-float { animation: float 3s ease-in-out infinite; }
      `}</style>
    </div>
  );
}
